<template>
  <div>
    <div class="f-flex f-justify-between">
      <BaseRadio
        ref="radio"
        name="shipping_method"
        :value="shippingMethod.shippingMethodResource.id"
        :id="$id('shipping-' + shippingMethod.shippingMethodResource.id)"
        @input="shippingMethodChanged"
        v-model="selected"
      >
        <span
          :aria-label="`${
            shippingMethod.shippingMethodResource.name
          }, cena: ${formattedPrice(price)}`"
        >
          {{ shippingMethod.shippingMethodResource.name }}
        </span>
        <ShippingPopover
          v-if="shippingDescription"
          :description="shippingDescription"
        ></ShippingPopover>
        <div v-show="freeOfCharge" class="f-text-sm">
          {{ formattedFreeOfCharge(freeOfCharge) }}
        </div>
      </BaseRadio>
      <div v-if="this.freeShipping">
        <div class="f-text-right f-text-xs f-line-through">
          {{ formattedPrice(price) }}
        </div>
        <div
          class="f-text-right"
          v-bind:class="{ 'f-text-red-600': this.freeShipping }"
        >
          {{ $t("prices.free").toUpperCase() }}
        </div>
      </div>
      <div v-if="!this.freeShipping">
        {{ formattedPrice(price) }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import FormShippingItem from "@/module/FormShippingItem";
import FormResource from "@/module/Form/Resources/FormResource";
import { isStringOrNull } from "@/module/ComponentPropsValidations/validations";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import money from "@/module/Filters/MoneyFilter";
import ShippingPopover from "@/components/Form/ShippingMethods/ShippingPopover.vue";
import NumberHelper from "@/module/NumberHelper";
import OrderPrices from "@/module/Order/OrderPrices";

interface ComponentData {
  selected: string | null;
  showShippingInfo: boolean;
  freeShipping: boolean;
}

export default Vue.extend({
  name: "OtherShipping",
  components: {
    ShippingPopover,
    BaseRadio,
  },
  props: {
    shippingMethod: {
      type: Object as PropType<FormShippingItem>,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    fullInvoiceOrIsAddress: {
      type: Boolean,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    order: {
      type: Object as PropType<OrderPrices>,
      required: true,
    },
    totalPriceIncVat: {
      required: true,
    },
  },

  data(): ComponentData {
    return {
      selected: null,
      showShippingInfo: false,
      freeShipping: false,
    };
  },

  computed: {
    freeOfCharge(): number | null {
      if (this.shippingMethod.freeOfCharges.hasOwnProperty("noFreeShipping")) {
        return null;
      }

      const shipping_form_item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (shipping_form_item === undefined) {
        return null;
      }

      const price = this.shippingMethod.freeOfCharges[this.currency];
      const includingVat =
        this.shippingMethod.shippingMethodResource.including_vat;
      const currentVat = shipping_form_item.currentVat;

      if (this.formResource.show_prices_including_vat) {
        return NumberHelper.priceIncludingVat(price, currentVat, includingVat);
      }
      return NumberHelper.priceWithOutVat(price, currentVat, includingVat);
    },

    price(): number {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      if (item === undefined) {
        return 0;
      }

      if (this.freeOfCharge !== null) {
        this.updateFreeShipping(this.isEligibleForFreeShipping());
      }

      if (this.formResource.show_prices_including_vat) {
        return item.currentPriceIncVat;
      }

      return item.currentPrice;
    },

    shippingDescription(): string | null {
      return this.shippingMethod.shippingMethodResource.description;
    },
  },

  methods: {
    formattedFreeOfCharge(price: number): string {
      if (price) {
        return (
          String(this.$t("shipping_method.free_shipping_from")) +
          money(price, this.$root.$i18n)
        );
      }
      return "";
    },

    formattedPrice(price: number): string {
      return money(price, this.$root.$i18n);
    },

    updateFreeShipping(value: boolean): void {
      this.freeShipping = value;
    },

    isEligibleForFreeShippingHelper(vat: boolean): number {
      if (vat) {
        return this.order.totalPriceWoShippingIncludingVat;
      }
      return this.order.totalPriceWoShipping;
    },

    isEligibleForFreeShipping(): boolean {
      if (this.freeOfCharge !== null) {
        if (
          this.isEligibleForFreeShippingHelper(
            this.formResource.show_prices_including_vat,
          ) >= this.freeOfCharge
        ) {
          return true;
        }
      }
      return false;
    },

    shippingMethodChanged(): void {
      this.$emit("shippingMethodChanged", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        shipping_metadata: null,
        shipping_form_item: this.shippingMethod.formItems.find((item) => {
          return item.name === this.country;
        }),
      });
      this.$emit("isEligibleForFreeShipping", {
        shipping_method_id: this.shippingMethod.shippingMethodResource.id,
        is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
      });
    },

    triggerVisibility(): void {
      const item = this.shippingMethod.formItems.find((item) => {
        return item.name === this.country;
      });

      const visible = item !== undefined;

      this.$emit("shippingMethodVisibilityChanged", { visible: visible });
    },
  },

  watch: {
    country(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
    },

    totalPriceIncVat(): void {
      if (this.selected) {
        this.$emit("isEligibleForFreeShipping", {
          shipping_method_id: this.shippingMethod.shippingMethodResource.id,
          is_eligible_for_free_shipping: this.isEligibleForFreeShipping(),
        });
      }
    },

    fullInvoiceOrIsAddress(): void {
      this.triggerVisibility();
      this.$emit("shippingMethodChanged", null);
      this.selected = null;
    },
  },

  mounted(): void {
    this.triggerVisibility();
  },
});
</script>

<style lang="scss"></style>
